<template>
  <div class="amc-past-papers-container">
    <!-- 顶部导航栏 -->
    <div class="amc-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">{{ selectedYear ? (selectedYear + ' ' + tt('pageTitle')) : tt('pageTitle') }}</h1>
      </div>
      <div class="header-right">
        <el-radio-group v-model="langMode" size="small" style="margin-right: 10px;">
          <el-radio-button label="zh">中文</el-radio-button>
          <el-radio-button label="en">English</el-radio-button>
          <el-radio-button label="bi">双语</el-radio-button>
        </el-radio-group>
        <div v-if="selectedYear && practiceQuestions.length" class="timer-bar" style="display:flex;align-items:center;gap:8px;margin-right:10px;">
          <span class="timer-display">{{ tt('elapsed') }}: {{ formatTime(elapsedSeconds) }}</span>
          <el-button-group>
            <el-button size="mini" icon="el-icon-caret-right" @click="startTimer" :disabled="timerRunning">{{ tt('start') }}</el-button>
            <el-button size="mini" icon="el-icon-video-pause" @click="pauseTimer" :disabled="!timerRunning">{{ tt('pause') }}</el-button>
            <el-button size="mini" icon="el-icon-refresh" @click="resetTimer">{{ tt('reset') }}</el-button>
          </el-button-group>
        </div>
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          @click="refreshQuestions"
          :loading="loading">
          {{ tt('refresh') }}
        </el-button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div
      v-if="loading"
      class="loading-container"
      v-loading="loading"
      :element-loading-text="tt('loading')"
      element-loading-background="rgba(0, 0, 0, 0.8)"
    ></div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-content">
        <i class="el-icon-warning error-icon"></i>
        <p class="error-message">{{ error }}</p>
        <el-button type="primary" @click="refreshQuestions">{{ tt('reload') }}</el-button>
      </div>
    </div>

    <!-- 年份选择 -->
    <div v-else-if="availableYears.length > 0 && !selectedYear" class="year-select-content">
      <div class="year-grid">
        <div
          v-for="(y, index) in availableYears"
          :key="y"
          class="year-card"
          :class="{
            'year-card-completed': isYearCompleted(y),
            'year-card-locked': isYearLocked(index)
          }"
          @click="handleYearCardClick(y, index)"
        >
          <div class="year-badge">
            <i v-if="isYearLocked(index)" class="el-icon-lock" style="margin-right:4px;"></i>
            {{ formatYearBadge(y) }}
          </div>
          <div class="year-info">
            <div class="year-title">{{ formatYearLabel(y) }}</div>
            <div class="year-sub">{{ tt('questionCount') }}：{{ formatYearCount(y) }}</div>
            <!-- 完成状态标识 -->
            <div v-if="getYearStatus(y) && (isYearCompleted(y) || (getYearStatus(y).answeredCount && getYearStatus(y).answeredCount > 0))" class="year-status">
              <el-tag v-if="isYearCompleted(y)" type="success" size="mini">
                <i class="el-icon-check"></i> {{ tt('completed') || '已完成' }}
              </el-tag>
              <el-tag v-else type="warning" size="mini">
                <i class="el-icon-time"></i> {{ tt('inProgress') || '进行中' }}
              </el-tag>
              <!-- 显示得分率 -->
              <span v-if="getYearStatus(y).scoreRate != null" class="score-rate">
                {{ tt('scoreRate') || '得分率' }}: {{ getYearStatus(y).scoreRate }}%
              </span>
            </div>
          </div>
          <el-button
            class="enter-btn"
            :type="isYearLocked(index) ? 'info' : (isYearCompleted(y) ? 'success' : 'primary')"
            size="mini"
            :disabled="isYearLocked(index)"
            @click.stop="handleYearActionClick(y, index)"
          >
            <i v-if="isYearLocked(index)" class="el-icon-lock" style="margin-right:4px;"></i>
            {{ isYearLocked(index) ? '会员专享' : (isYearCompleted(y) ? (tt('reviewAgain') || '再次练习') : (tt('startPractice'))) }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 题目练习区域（按年份） -->
    <div v-else-if="selectedYear && practiceQuestions.length > 0" class="question-practice-content">
      <!-- 返回年份列表 -->
      <div class="filter-section">
        <el-button type="text" @click="backToYearSelect">← {{ tt('backToYears') }}</el-button>
      </div>

      <!-- 题目总览导航栏 -->
      <div class="question-nav-panel" :class="{ collapsed: navCollapsed }">
        <div class="nav-header" @click="navCollapsed = !navCollapsed">
          <span class="nav-title">
            <i :class="navCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
            {{ tt('questionOverview') || '题目总览' }}
          </span>
          <div class="nav-overview-stats">
            <div class="nav-overview-stat-item">
              <div class="nav-overview-stat-number">{{ practiceQuestions.length }}</div>
              <div class="nav-overview-stat-label">{{ tt('totalQuestions') }}</div>
            </div>
            <div class="nav-overview-stat-item">
              <div class="nav-overview-stat-number">{{ answeredCount }}</div>
              <div class="nav-overview-stat-label">{{ tt('answeredCount') }}</div>
            </div>
            <div class="nav-overview-stat-item">
              <div class="nav-overview-stat-number">{{ correctCount }}</div>
              <div class="nav-overview-stat-label">{{ tt('correctCount') }}</div>
            </div>
            <div class="nav-overview-stat-item">
              <div class="nav-overview-stat-number">{{ accuracy }}%</div>
              <div class="nav-overview-stat-label">{{ tt('accuracy') }}</div>
            </div>
            <div class="nav-overview-stat-item">
              <div class="nav-overview-stat-number">{{ formatTime(elapsedSeconds) }}</div>
              <div class="nav-overview-stat-label">{{ tt('elapsed') }}</div>
            </div>
          </div>
          <span class="nav-stats">{{ answeredCount }} / {{ practiceQuestions.length }}</span>
        </div>
        <div class="nav-content" v-show="!navCollapsed">
          <div class="nav-grid">
            <div
              v-for="(q, idx) in practiceQuestions"
              :key="q.id"
              class="nav-item"
              :class="{
                active: idx === currentIndex,
                answered: userAnswers[idx] && String(userAnswers[idx]).trim() !== '',
                correct: questionResults[idx] && questionResults[idx].isCorrect,
                wrong: questionResults[idx] && !questionResults[idx].isCorrect
              }"
              @click="jumpToQuestion(idx)"
            >
              <span class="nav-item-number">{{ idx + 1 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 题目列表 -->
      <div class="questions-container">
        <div
          v-for="(question, index) in practiceQuestions"
          :key="question.id"
          class="question-item"
          :class="{
            'answered': userAnswers[index],
            'submitted-wrong': hasSubmitted && getQuestionResult(index) && !getQuestionResult(index).isCorrect,
            'submitted-correct': hasSubmitted && getQuestionResult(index) && getQuestionResult(index).isCorrect
          }"
          v-show="index === currentIndex"
        >
          <div class="question-header">
            <span class="question-number">{{ formatQuestionNumber(index) }}</span>
            <div class="question-meta">
              <span class="question-year" v-if="question.year || question.type">{{ formatYearTag(question.year || question.type) }}</span>
            </div>
          </div>

          <div class="question-content" v-html="renderMathFormula(getQuestionContent(question, langMode))"></div>

          <!-- 题目图片（支持多张） -->
          <div class="question-stem-images" v-if="getQuestionImages(question).length > 0">
            <el-image
              v-for="(imgUrl, imgIdx) in getQuestionImages(question)"
              :key="imgIdx"
              :src="resolveImageUrl(imgUrl)"
              fit="contain"
              class="stem-image"
              :preview-src-list="getQuestionImages(question).map(u => resolveImageUrl(u))"
            />
          </div>

          <!-- 图片上传区域 -->
          <div class="image-upload-section">
            <div class="upload-button-container">
              <el-upload
                class="image-uploader"
                action="#"
                :auto-upload="false"
                accept="image/*"
                :limit="1"
                :multiple="false"
                :file-list="imageFileLists[index]"
                :before-upload="handleBeforeUpload"
                :on-change="(file, fileList) => handleFileChange(index, file, fileList)"
                :on-remove="() => handleRemoveImage(index)"
              >
                <el-button type="primary" icon="el-icon-upload" size="small">{{ tt('uploadImage') }}</el-button>
                <div slot="tip" class="el-upload__tip">{{ tt('imageOnlyTip') }}</div>
              </el-upload>
              <span class="upload-tip">{{ tt('uploadStepsTip') }}</span>
            </div>
          </div>

          <!-- 已上传的图片预览 -->
          <div v-if="questionImages[index] && questionImages[index].length > 0" class="uploaded-images">
            <div class="image-preview-title">{{ tt('uploadedImages') }}</div>
            <div class="image-list">
              <div
                v-for="(image, imgIndex) in questionImages[index]"
                :key="imgIndex"
                class="image-item">
                <img :src="image.url" :alt="tt('previewImageAlt') + (imgIndex + 1)" />
                <div class="image-actions">
                  <el-button
                    type="text"
                    icon="el-icon-view"
                    size="mini"
                    @click="previewImage(image.url)">
                    {{ tt('preview') }}
                  </el-button>
                  <el-button
                    type="text"
                    icon="el-icon-delete"
                    size="mini"
                    @click="removeImage(index, imgIndex)">
                    {{ tt('delete') }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <!-- 选项显示 -->
          <div class="question-options" v-if="getOptions(question).length > 0">
            <!-- 如果有选项图片，显示图片选项 -->
            <div v-if="hasOptionImages(question)">
              <el-radio-group v-model="userAnswers[index]" @change="onAnswerChange(index)" :disabled="hasSubmitted">
                <el-radio
                  v-for="(option, optIdx) in getOptions(question)"
                  :key="option.key"
                  :label="option.key"
                  class="option-item option-with-image"
                >
                  <span class="option-key">{{ option.key }}.</span>
                  <el-image
                    v-if="getOptionImage(question, optIdx)"
                    :src="resolveImageUrl(getOptionImage(question, optIdx))"
                    fit="contain"
                    style="max-width: 150px; max-height: 200px; margin-left: 8px; display: block;"
                    :preview-src-list="[resolveImageUrl(getOptionImage(question, optIdx))]"
                  />
                </el-radio>
              </el-radio-group>
            </div>
            <!-- 否则显示文字选项 -->
            <div v-else>
              <el-radio-group v-model="userAnswers[index]" @change="onAnswerChange(index)" :disabled="hasSubmitted">
                <el-radio
                  v-for="option in getOptions(question)"
                  :key="option.key"
                  :label="option.key"
                  class="option-item"
                >
                  <span v-html="renderMathFormula(option.key + '. ' + option.value)"></span>
                </el-radio>
              </el-radio-group>
            </div>
          </div>

          <!-- 如果没有选项，显示答案输入框 -->
          <div class="question-answer-input" v-else>
            <el-input
              v-model="userAnswers[index]"
              @input="onAnswerChange(index)"
              :placeholder="tt('inputAnswer')"
              :disabled="hasSubmitted"
              maxlength="10"
              show-word-limit
              style="width: 200px"
            ></el-input>
            <span class="answer-tip">{{ tt('answerTip') }}</span>
          </div>

          <!-- 提交后：错题展示正确答案与解析 -->
          <div v-if="hasSubmitted && getQuestionResult(index) && !getQuestionResult(index).isCorrect" class="post-submit-panel">
            <div class="post-submit-answer">
              <span class="correct-label">{{ tt('correctAnswer') }}</span>
              <span class="correct-value">{{ question.answer }}</span>
            </div>
            <div v-if="getQuestionAnalysis(question)" class="analysis-section">
              <div class="analysis-title">{{ tt('analysis') }}</div>
              <div class="analysis-content" v-html="renderMathFormula(getQuestionAnalysis(question))"></div>
            </div>
          </div>

          <!-- 单题导航 -->
          <div class="question-nav">
            <el-button size="small" @click="prevQuestion" :disabled="currentIndex === 0">{{ tt('prev') }}</el-button>
            <el-button size="small" type="primary" @click="nextQuestion" :disabled="currentIndex >= practiceQuestions.length - 1">{{ tt('next') }}</el-button>
            <span class="question-progress">{{ tt('question') }} {{ currentIndex + 1 }} / {{ practiceQuestions.length }}</span>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-buttons">
        <el-button
          type="primary"
          size="large"
          @click="submitAnswers"
          :disabled="answeredCount === 0"
          :loading="submitting">
          {{ tt('submit') }}
        </el-button>
        <el-button
          type="warning"
          size="large"
          @click="resetAnswers">
          {{ tt('reset') }}
        </el-button>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-state">
      <i class="el-icon-document"></i>
      <p>{{ tt('empty') }}</p>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog
      :title="tt('imagePreview')"
      :visible.sync="showImagePreview"
      width="auto"
      :close-on-click-modal="true">
      <div class="image-preview-dialog">
        <img :src="previewImageUrl" alt="预览图片" class="preview-image" />
      </div>
    </el-dialog>

    <!-- 结果弹窗 -->
    <el-dialog
      :title="tt('resultTitle')"
      :visible.sync="showResultDialog"
      width="80%"
      :before-close="handleCloseResultDialog"
    >
      <div class="result-summary">
        <div class="result-stats">
          <div class="result-stat-item">
            <span class="stat-label">{{ tt('totalLabel') }}</span>
            <span class="stat-value">{{ practiceQuestions.length }}</span>
          </div>
          <div class="result-stat-item">
            <span class="stat-label">{{ tt('answeredLabel') }}</span>
            <span class="stat-value">{{ answeredCount }}</span>
          </div>
          <div class="result-stat-item">
            <span class="stat-label">{{ tt('correctLabel') }}</span>
            <span class="stat-value correct">{{ correctCount }}</span>
          </div>
          <div class="result-stat-item">
            <span class="stat-label">{{ tt('wrongLabel') }}</span>
            <span class="stat-value wrong">{{ wrongCount }}</span>
          </div>
          <div class="result-stat-item">
            <span class="stat-label">{{ tt('accuracyLabel') }}</span>
            <span class="stat-value">{{ accuracy }}%</span>
          </div>
          <div class="result-stat-item">
            <span class="stat-label">{{ tt('finalScoreLabel') }}</span>
            <span class="stat-value score">{{ finalScore }}{{ scoreUnit }}</span>
          </div>
          <div class="result-stat-item">
            <span class="stat-label">{{ tt('elapsed') }}</span>
            <span class="stat-value">{{ formatTime(elapsedSeconds) }}</span>
          </div>
        </div>
      </div>

      <!-- 错题列表 -->
      <div v-if="wrongQuestions.length > 0" class="wrong-questions-section">
        <h3>{{ tt('wrongAnalysis') }}</h3>
        <div
          v-for="result in wrongQuestions"
          :key="result.questionId"
          class="wrong-question-item"
        >
          <div class="question-header">
            <span class="question-number">{{ formatQuestionNumber(getOriginalQuestionIndex(result.questionId)) }}</span>
            <span class="question-knowledge">{{ result.knowledge }}</span>
          </div>

          <div class="question-content" v-html="result.questionContent"></div>

          <div class="answer-details">
            <div class="answer-row">
              <span class="answer-label">{{ tt('yourAnswer') }}</span>
              <span class="answer-value incorrect">{{ result.userAnswer }}</span>
            </div>
            <div class="answer-row">
              <span class="answer-label">{{ tt('correctAnswer') }}</span>
              <span class="answer-value correct">{{ result.correctAnswer }}</span>
            </div>
          </div>

          <div class="analysis-section" v-if="result.analysis">
            <div class="analysis-title">{{ tt('analysis') }}</div>
            <div class="analysis-content" v-html="renderMathFormula(result.analysis)"></div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="showResultDialog = false">{{ tt('close') }}</el-button>
        <el-button type="primary" @click="retryPractice">{{ tt('retry') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { listYearsByRepoId, pagingByRepoId } from '@/api/courseQuestion'
import { pagingGespQuestions } from '@/api/gespQuestion'
import { judgeSolution } from '@/api/ai'
import { addRecord, deleteRecordsByQuesIds } from '@/api/userQuestionRecord'
import { getPastPaperYearStatus, startPastPaperExam, finishPastPaperExam, deletePastPaperByYear, deletePastPaperById, listPastPaperRecords } from '@/api/pastPaperRecord'
import { userStatusAdd, userStatusBatchDel, userStatusByUser } from '@/api/userStatus'
import { getProgressByDate, upsertTodayPlan, getProgressPaging, updateProgressFinished, getSchemeByUserIdAndRepoId, updateSchemeFinishedIds } from '@/api/studyPlan'
import { getTokenInfo } from '@/utils/jwtUtils'
import { getMinioUrl } from '@/utils/minio'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'AMCPastPapers',
  data() {
    return {
      // 加载状态
      loading: false,
      // 错误信息
      error: null,
      // 题目列表
      questionList: [],
      // 当前年份题目列表（改为服务端按年拉取）
      practiceQuestions: [],
      // 用户答案
      userAnswers: [],
      // 提交状态
      submitting: false,
      // 是否显示答案
      showAnswers: false,
      // 结果弹窗
      showResultDialog: false,
      // 答题结果
      questionResults: [],
      // 错题列表
      wrongQuestions: [],

      // AI 判题弹窗（可按需使用）
      showAiAnalysisDialog: false,
      aiAnalysisResult: null,

      // 筛选条件
      selectedYear: '',
      selectedDifficulty: '',
      availableYears: [],
      yearCounts: {},
      usageLimit: 0, // 使用限制（0表示不限制）

      // 从路由参数获取的repoId
      repoId: null,

      // 图片上传相关
      questionImages: [], // 保留旧结构（不再使用网络上传）
      uploadedImageFiles: [], // 每题本地 File（不走上传接口）
      imageFileLists: [], // 每题的本地文件列表用于展示
      showImagePreview: false,
      previewImageUrl: '',
      uploadUrl: '/api/upload/image', // 根据实际后端接口调整
      // 语言模式：zh/en/bi
      langMode: 'bi',
      // 当前显示题目索引（单题模式）
      currentIndex: 0,
      // 计时器
      elapsedSeconds: 0,
      timerRunning: false,
      timerId: null,
      // 每题计时
      questionStartTimes: [], // 每题开始时间戳
      questionDurations: [], // 每题用时（秒）
      // 年份完成状态记录
      yearStatusMap: {}, // { year: AmcPastPaperRecord }
      currentExamRecord: null, // 当前考试记录
      // 题目导航栏折叠状态
      navCollapsed: false,
      // 是否已提交（用于退出提示）
      hasSubmitted: false,
      statProgressId: null
    }
  },
  watch: {
    // 监听当前题目变化，更新全局上下文供AI助手使用
    currentIndex: {
      handler(newVal) {
        const q = this.practiceQuestions && this.practiceQuestions[newVal]
        if (q) {
          const ctx = {
            id: q.id,
            content: q.content || q.contentCn || q.contentEn || '',
            answer: q.answer,
            analysis: q.analysis || q.analysisCn || q.analysisEn || '',
            knowledge: q.knowledge || q.knowledgeName || '',
            year: this.selectedYear
          }
          window.__currentQuestionContext = ctx
          try {
            localStorage.setItem('currentQuestionContext', JSON.stringify(ctx))
          } catch (e) {}
        }
      },
      immediate: true
    }
  },
  computed: {
    // 动态获取上传头部信息
    uploadHeaders() {
      return {
        'Authorization': `Bearer ${this.$store.getters.token}`
      }
    },

    // 将本次历年真题作答写入 user-question-records，并写入 amc-status 汇总
    async persistPastPapersRecords() {
      try {
        const user = getTokenInfo()
        const userId = user?.id
        if (!userId) return
        const answeredResults = Array.isArray(this.questionResults) ? this.questionResults : []
        if (!answeredResults.length) return

        const now = new Date().toISOString()
        const promises = []

        for (const r of answeredResults) {
          const q = this.practiceQuestions.find(item => item?.id === r.questionId)
          const questionIndex = this.practiceQuestions.findIndex(item => item?.id === r.questionId)
          const aiStep = r?.aiInfo?.analysis?.step_analysis || ''
          const aiSug = r?.aiInfo?.analysis?.suggestion || ''
          // 确保 judge 字段有值，优先使用题目的解析字段
          const baseJudge = q?.analysis || q?.analysisCn || q?.analysiscn || q?.analysisEn || q?.analysisen || q?.judge || ''
          const judgeText = aiStep ? (aiSug ? `${aiStep}\n建议：${aiSug}` : aiStep) : baseJudge

          // 获取题目中英文内容和用时
          const quesContentCn = q?.contentCn || q?.contentcn || q?.content || ''
          const quesContentEn = q?.contentEn || q?.contenten || ''
          const duration = (questionIndex >= 0 && this.questionDurations[questionIndex])
            ? String(this.questionDurations[questionIndex])
            : '0'

          // 知识点字段：如果是纯数字ID则保持，否则直接使用（可能已经是名称）
          let knowledgeStr = q?.knowledge || q?.knowledgeName || ''
          if (knowledgeStr && /^\d+$/.test(String(knowledgeStr).trim())) {
            // 是纯数字ID，可以转换为名称（如果有映射的话）
            // 暂时保持ID，等待后续优化
            knowledgeStr = String(knowledgeStr)
          }

          const payload = {
            userId: userId,
            quesId: q?.id,
            quescontent_cn: quesContentCn,
            quescontent_en: quesContentEn,
            duration: duration,
            userAnswer: this.getFullOptionContent(q, r.userAnswer),
            answer: q?.answer || '',
            judge: judgeText,
            mynote: '',
            isReview: 0,
            knowledge: knowledgeStr,
            isRight: r.isCorrect ? 1 : 0,
            repoId: Number(this.repoId) || this.repoId,
            time: now,
            aiAnalysis: r.aiInfo ? JSON.stringify({
              ai_analysis: {
                answer_correct: r.aiInfo.answerCorrect,
                step_analysis: r.aiInfo.analysis ? r.aiInfo.analysis.step_analysis || '' : '',
                suggestion: r.aiInfo.analysis ? r.aiInfo.analysis.suggestion || '' : ''
              }
            }) : null
          }
          promises.push(addRecord(payload))
        }

        // AMC 状态汇总
        const quesList = answeredResults.map(r => r.questionId).filter(Boolean).join(';')
        const rightCount = answeredResults.filter(r => r.isCorrect).length
        const amcPayload = {
          userId: userId,
          quesList,
          count: answeredResults.length,
          rightCount,
          source: '历年真题',
          repoId: Number(this.repoId) || this.repoId
        }
        promises.push(userStatusAdd(amcPayload))

        await Promise.allSettled(promises)
      } catch (e) {
        // 静默失败，避免打扰用户
        // console.warn('Persist past papers records failed:', e)
      }
    },
    // 已答题数
    answeredCount() {
      return this.userAnswers.filter(answer => String(answer || '').trim() !== '').length
    },

    allAnswered() {
      const total = Array.isArray(this.practiceQuestions) ? this.practiceQuestions.length : 0
      if (!total) return false
      return this.answeredCount >= total
    },
    // 答对题数
    correctCount() {
      return this.questionResults.filter(result => result.isCorrect).length
    },
    // 答错题数
    wrongCount() {
      return this.questionResults.filter(result => !result.isCorrect).length
    },
    // 正确率
    accuracy() {
      if (this.answeredCount === 0) return 0
      return Math.round((this.correctCount / this.answeredCount) * 100)
    },
    // 最终得分
    finalScore() {
      const total = this.practiceQuestions.length
      if (total === 0) return 0
      return Math.round((this.correctCount / total) * 100)
    },
    // 年份后缀（年份卡片用，题头仅显示数字）
    yearSuffix() {
      return this.langMode === 'zh' ? '年' : ''
    },
    // 分数单位
    scoreUnit() {
      return this.langMode === 'zh' ? '分' : ''
    }
  },
  created() {
    this.hideSidebarAndNavbar()
    this.initPage()
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  activated() {
    this.applyUsageLimitFromRoute()
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.showSidebarAndNavbar()
    this.clearTimer()
    // 离开页面时清除题目上下文
    window.__currentQuestionContext = null
    try {
      localStorage.removeItem('currentQuestionContext')
    } catch (e) {}
  },
  beforeRouteLeave(to, from, next) {
    if (this.shouldWarnOnExit()) {
      const ok = window.confirm('退出将导致本次作答作废，确定要退出吗？')
      next(ok)
      return
    }
    next()
  },
  methods: {
    getQuestionResult(index) {
      const arr = Array.isArray(this.questionResults) ? this.questionResults : []
      const i = Number(index)
      if (!Number.isFinite(i) || i < 0) return null
      return arr[i] || null
    },
    shouldWarnOnExit() {
      if (!this.selectedYear) return false
      if (this.submitting) return false
      if (this.hasSubmitted) return false
      return this.answeredCount > 0
    },
    handleBeforeUnload(e) {
      if (!this.shouldWarnOnExit()) return
      e.preventDefault()
      e.returnValue = ''
    },
    getTodayDate() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    async syncStudyProgressBatch(results) {
      try {
        const user = getTokenInfo()
        const userId = user?.id
        const repoIdVal = Number(this.repoId) || this.repoId
        if (!userId || !repoIdVal) return

        const normalizedResults = Array.isArray(results) ? results : []
        const questionIds = normalizedResults
          .map(item => Number(item && item.questionId))
          .filter(id => !isNaN(id) && id > 0)
        if (!questionIds.length) return

        try {
          const schemeResp = await getSchemeByUserIdAndRepoId(userId, repoIdVal)
          const scheme = schemeResp?.data
          const schemeId = scheme ? (scheme.id ?? scheme.schemeId) : null
          if (schemeId) {
            const currentFinished = String(scheme?.finishedId || scheme?.finished_id || '')
            const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))
            questionIds.forEach(id => finishedSet.add(id))
            const merged = Array.from(finishedSet).filter(id => !isNaN(id) && id > 0).join(',')
            await updateSchemeFinishedIds(schemeId, merged)
          }
        } catch (schemeErr) {
          console.error('同步学习方案失败:', schemeErr)
        }

        const todayStr = this.getTodayDate()
        let progressId = this.statProgressId
        if (!progressId) {
          const idRes = await getProgressByDate(userId, repoIdVal, todayStr)
          const idData = idRes?.data?.data ?? idRes?.data ?? idRes
          progressId = (typeof idData === 'number' || typeof idData === 'string') ? Number(idData) : (idData && idData.id)
        }
        if (!progressId) {
          const planRes = await upsertTodayPlan({ userId, repoId: repoIdVal, date: todayStr })
          const planData = planRes?.data?.data ?? planRes?.data ?? planRes
          progressId = planData && (planData.id || planData.ID)
        }
        this.statProgressId = progressId || this.statProgressId
        if (!progressId) return

        const page = await getProgressPaging({ pageNum: 1, pageSize: 1, userId, repoId: repoIdVal, dateFrom: todayStr, dateTo: todayStr })
        const pdata = page?.data?.data ?? page?.data ?? {}
        const records = Array.isArray(pdata.records) ? pdata.records : []
        const first = records && records.length ? records[0] : null
        if (!first) return

        const currentFinished = String(first.finished_id || first.finishedId || '')
        const finishedSet = new Set(currentFinished.split(/[，,;\s]+/).filter(x => x).map(Number))
        questionIds.forEach(id => finishedSet.add(id))

        const mergedFinished = Array.from(finishedSet).filter(id => !isNaN(id) && id > 0).join(',')
        const correctDelta = normalizedResults.filter(item => item && item.isCorrect).length
        const wrongDelta = normalizedResults.length - correctDelta
        const correctCount = Number(first.correct_count ?? first.correctCount ?? 0) + correctDelta
        const wrongCount = Number(first.wrong_count ?? first.wrongCount ?? 0) + wrongDelta
        const reviewDone = Number(first.review_done ?? first.reviewDone ?? 0)
        await updateProgressFinished(progressId, mergedFinished, correctCount, wrongCount, reviewDone)
      } catch (e) {
        console.error('同步学习进度失败:', e)
      }
    },
    // 隐藏侧边栏和导航栏
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = 'none'
      }
      if (navbar) {
        navbar.style.display = 'none'
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },

    // 恢复侧边栏和导航栏
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = ''
      }
      if (navbar) {
        navbar.style.display = ''
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },

    // 初始化页面
    initPage() {
      // 从路由参数获取repoId
      this.repoId = this.$route.query.repoId
      this.applyUsageLimitFromRoute()

      if (!this.repoId) {
        this.$message.error('缺少题库ID参数')
        this.goBack()
        return
      }

      this.loadYears()
    },

    // 加载年份列表（服务端）
    // 后端返回扁平格式: { "2025": 100 } 或 { "2025_3": 25 } 或 { "2025_A": 50 }
    async loadYears() {
      this.loading = true
      this.error = null
      try {
        const courseType = this.$route.query.courseType || ''
        const resp = await listYearsByRepoId(this.repoId, courseType)
        const raw = resp?.data?.data ?? resp?.data ?? {}
        if (typeof raw === 'object' && !Array.isArray(raw)) {
          this.yearCounts = raw
          this.availableYears = Object.keys(raw).sort((a, b) => b.localeCompare(a))
        } else {
          const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.list) ? raw.list : [])
          this.availableYears = list.map(String).sort((a, b) => b.localeCompare(a))
          this.yearCounts = {}
        }
        await this.mergeGespCodeCounts(courseType)
        await this.loadYearStatus()

        // 应用使用限制标记（如果有）
        this.applyUsageLimitFromRoute()
      } catch (e) {
        console.error('获取年份失败:', e)
        this.error = '获取年份失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    extractGespLevel(courseType) {
      const s = String(courseType || '').trim().toUpperCase()
      if (!s) return null
      let m = s.match(/GESP\s*LEVEL\s*([1-8])/)
      if (m) return Number(m[1])
      m = s.match(/GESP([1-8])/)
      if (m) return Number(m[1])
      return null
    },
    resolveGespLevel(courseType) {
      const routeLevel = Number(this.$route?.query?.level)
      if (Number.isFinite(routeLevel) && routeLevel > 0) return routeLevel
      return this.extractGespLevel(courseType)
    },
    async mergeGespCodeCounts(courseType) {
      const ct = String(courseType || '').trim().toUpperCase()
      if (!ct.includes('GESP')) return
      const level = this.resolveGespLevel(ct)
      const keys = (Array.isArray(this.availableYears) ? this.availableYears : []).map(String)
      if (!keys.length) return
      const nextCounts = { ...(this.yearCounts || {}) }
      const requestCache = new Map()
      await Promise.all(keys.map(async (key) => {
        const parts = String(key).split('_')
        const year = parts[0]
        const sub = parts.length > 1 ? parts.slice(1).join('_') : ''
        const isMonthSub = sub && /^\d+$/.test(sub)
        const params = {
          pageNum: 1,
          pageSize: 2000,
          repoId: 27,
          year: isMonthSub ? undefined : year
        }
        if (level != null && level > 0) params.level = level
        const requestKey = JSON.stringify({
          repoId: params.repoId,
          year: params.year || '',
          level: params.level || ''
        })
        try {
          if (!requestCache.has(requestKey)) {
            requestCache.set(requestKey, (async () => {
              const resp = await pagingGespQuestions(params)
              const raw = resp?.data?.data ?? resp?.data ?? {}
              return Array.isArray(raw?.records)
                ? raw.records
                : (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : []))
            })())
          }
          const records = await requestCache.get(requestKey)

          // 根据当前等级过滤题目（从number字段中提取等级信息）
          let filteredRecords = records
          if (level) {
            const levelPrefix = `GESP${level}`
            filteredRecords = records.filter(q => {
              const num = String(q.number || '').trim().toUpperCase()
              return num.startsWith(levelPrefix)
            })
          }

          // 按年月过滤：number 格式为 GESP{level}{YYYYMM}{seq}，如 GESP120250927
          if (isMonthSub) {
            const mm = String(sub).padStart(2, '0')
            const yearMonth = `${year}${mm}`
            filteredRecords = filteredRecords.filter(q => {
              const num = String(q.number || '').trim()
              return num.includes(yearMonth)
            })
          } else {
            // 仅按年份过滤
            filteredRecords = filteredRecords.filter(q => {
              const num = String(q.number || '').trim()
              return num.includes(year)
            })
          }

          const codeCount = filteredRecords.length
          const base = Number(nextCounts[key] ?? 0)
          nextCounts[key] = (Number.isFinite(base) ? base : 0) + (Number.isFinite(codeCount) ? codeCount : 0)
        } catch (e) {
          const base = Number(nextCounts[key] ?? 0)
          nextCounts[key] = Number.isFinite(base) ? base : 0
        }
      }))
      this.yearCounts = nextCounts
    },

    // 加载年份完成状态
    async loadYearStatus() {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId || !this.repoId) return

        const courseType = (this.$route.query.courseType || '').toUpperCase()
        if (courseType.includes('GESP')) {
          const resp = await listPastPaperRecords(userId, this.repoId)
          const raw = resp?.data?.data ?? resp?.data ?? []
          const records = Array.isArray(raw) ? raw : []
          const nextMap = {}
          records.forEach(record => {
            if (!record || !record.year) return
            const detail = this.parsePastPaperAnswerDetail(record.answerDetail || record.answer_detail)
            const meta = detail && detail.meta && typeof detail.meta === 'object' ? detail.meta : {}
            const month = meta.month != null && String(meta.month) !== '' ? String(meta.month) : ''
            const level = meta.level != null && String(meta.level) !== '' ? String(meta.level) : ''
            if (!month) return
            const mapKey = `${record.year}_${month}${level ? '_' + level : ''}`
            const existing = nextMap[mapKey]
            if (!existing || Number(record.status) > Number(existing.status || 0) || new Date(record.updateTime || 0).getTime() > new Date(existing.updateTime || 0).getTime()) {
              nextMap[mapKey] = record
            }
          })
          this.yearStatusMap = nextMap
          return
        }

        const resp = await getPastPaperYearStatus(userId, this.repoId)
        this.yearStatusMap = resp?.data?.data ?? resp?.data ?? {}
      } catch (e) {
        console.warn('加载年份完成状态失败:', e)
        this.yearStatusMap = {}
      }
    },

    parsePastPaperAnswerDetail(answerDetail) {
      if (!answerDetail) return null
      try {
        const detail = typeof answerDetail === 'string' ? JSON.parse(answerDetail) : answerDetail
        return detail && typeof detail === 'object' ? detail : null
      } catch (e) {
        return null
      }
    },

    // 获取年份完成状态
    getYearStatus(year) {
      const statusKey = this.resolveYearStatusKey(year)
      const record = this.yearStatusMap[String(statusKey)]
      if (!record) return null
      return record
    },

    resolveYearStatusKey(year) {
      const courseType = (this.$route.query.courseType || '').toUpperCase()
      const key = String(year)
      if (courseType.includes('GESP') && key.includes('_')) {
        const parts = key.split('_')
        const routeLevel = this.resolveGespLevel(courseType)
        return routeLevel ? `${parts[0]}_${parts[1]}_${routeLevel}` : `${parts[0]}_${parts[1]}`
      }
      return key
    },

    // 判断年份是否已完成
    isYearCompleted(year) {
      const record = this.getYearStatus(year)
      return record && Number(record.status) === 1
    },

    // 判断年份是否被锁定（超出使用限制）
    isYearLocked(index) {
      const limit = this.getEffectiveUsageLimit()
      return limit > 0 && index >= limit
    },

    getEffectiveUsageLimit() {
      const routeLimit = this.$route && this.$route.query && this.$route.query.limit
        ? Number(this.$route.query.limit)
        : 0
      if (routeLimit > 0) return routeLimit
      return Number(this.usageLimit) || 0
    },

    applyUsageLimitFromRoute() {
      const routeLimit = this.$route && this.$route.query && this.$route.query.limit
        ? Number(this.$route.query.limit)
        : 0
      this.usageLimit = routeLimit > 0 ? routeLimit : 0
    },

    // 显示会员提示
    showMemberTip() {
      this.$confirm('此年份为会员专享内容，开通会员后可查看全部年份的真题。', '会员专享', {
        confirmButtonText: '开通会员',
        cancelButtonText: '暂不开通',
        type: 'warning'
      }).then(() => {
        // 跳转到会员购买页面或返回课程中心
        this.$router.push('/amc-course')
      }).catch(() => {})
    },

    handleYearCardClick(key, index) {
      if (this.isYearLocked(index)) {
        this.showMemberTip()
        return
      }
      this.openYearDetail(key)
    },

    handleYearActionClick(key, index) {
      if (this.isYearLocked(index)) {
        this.showMemberTip()
        return
      }
      if (this.isYearCompleted(key)) {
        this.retryYearPractice(key)
        return
      }
      this.openYearDetail(key)
    },

    openYearDetail(key) {
      // GESP 跳转到专用答题界面
      const courseType = (this.$route.query.courseType || '').toUpperCase()
      if (courseType.includes('GESP')) {
        this.enterYear(key)
        return
      }
      this.selectedYear = key
      this.loadYearQuestions(key)
    },

    async retryYearPractice(key) {
      // GESP 跳转到专用答题界面
      const courseType = (this.$route.query.courseType || '').toUpperCase()
      if (courseType.includes('GESP')) {
        const confirmed = await this.$confirm(
          '重新练习将清除当前这套真题在历年记录表和做题记录表中的记录，确定继续吗？',
          '重新练习',
          { type: 'warning', confirmButtonText: '确定', cancelButtonText: '取消' }
        ).catch(() => false)
        if (!confirmed) return
        await this.clearGespYearRecords(key)
        this.$router.push({
          path: '/gesp-practice',
          query: {
            repoId: this.repoId,
            year: String(key).split('_')[0],
            ...(String(key).split('_').length > 1 && /^\d+$/.test(String(key).split('_').slice(1).join('_'))
              ? { month: String(key).split('_').slice(1).join('_') }
              : {}),
            ...(this.resolveGespLevel(courseType) ? { level: this.resolveGespLevel(courseType) } : {}),
            courseType: this.$route.query.courseType || undefined,
            courseTypeId: this.$route.query.courseTypeId || undefined,
            courseTypeCode: this.$route.query.courseTypeCode || undefined,
            limit: this.$route.query.limit || undefined,
            retry: '1'
          }
        })
        return
      }
      const confirmed = await this.$confirm(
        '再次练习将清除本年份的做题记录和完成状态，确定继续吗？',
        '再次练习',
        { type: 'warning', confirmButtonText: '确定', cancelButtonText: '取消' }
      ).catch(() => false)
      if (!confirmed) return

      this.selectedYear = key
      await this.loadYearQuestions(key)
      await this.clearYearRecords(key)
      this.resetAnswers()
    },

    // 进入某年份/月份/卷别练习，key 格式: "2025"(普通) / "2025_3"(GESP月份) / "2025_A"(AB卷)
    async enterYear(key) {
      const courseType = (this.$route.query.courseType || '').toUpperCase()
      const parts = String(key).split('_')

      const year = parts[0]
      const sub = parts.length > 1 ? parts.slice(1).join('_') : ''

      // GESP 跳转到专用答题界面
      if (courseType.includes('GESP')) {
        const gespLevel = this.resolveGespLevel(courseType)
        this.$router.push({
          path: '/gesp-practice',
          query: {
            repoId: this.repoId,
            year: year,
            ...(sub && /^\d+$/.test(sub) ? { month: sub } : {}),
            ...(gespLevel ? { level: gespLevel } : {}),
            courseType: this.$route.query.courseType || undefined,
            courseTypeId: this.$route.query.courseTypeId || undefined,
            courseTypeCode: this.$route.query.courseTypeCode || undefined,
            limit: this.$route.query.limit || undefined,
            ...(this.$route.query.retry ? { retry: this.$route.query.retry } : {})
          }
        })
        return
      }

      this.openYearDetail(key)
    },

    // 返回年份选择
    backToYearSelect() {
      if (this.shouldWarnOnExit()) {
        const ok = window.confirm('退出将导致本次作答作废，确定要返回年份列表吗？')
        if (!ok) return
      }
      this.selectedYear = ''
      this.applyUsageLimitFromRoute()
      this.practiceQuestions = []
      this.userAnswers = []
      this.uploadedImageFiles = []
      this.imageFileLists = []
      this.questionResults = []
      this.wrongQuestions = []
      this.currentIndex = 0
      this.resetTimer()
    },
    // 加载题目，key 格式: "2025"(普通) / "2025_3"(GESP月份) / "2025_A"(AB卷)
    async loadYearQuestions(key) {
      this.loading = true
      this.error = null
      this.hasSubmitted = false
      const parts = String(key).split('_')
      const pureYear = parts[0]
      const sub = parts.length > 1 ? parts.slice(1).join('_') : ''
      const isMonthSub = sub && /^\d+$/.test(sub)
      try {
        const resp = await pagingByRepoId(this.repoId, {
          pageNum: 1,
          pageSize: 2000,
          year: isMonthSub ? undefined : pureYear
        })
        const raw = resp?.data?.data ?? resp?.data ?? {}
        let records = Array.isArray(raw?.records) ? raw.records : (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : []))
        if (sub) {
          if (/^\d+$/.test(sub)) {
            // GESP 月份过滤：number 中包含 "年份+两位月份"
            const mm = sub.padStart(2, '0')
            records = records.filter(q => {
              const num = String(q.number || '')
              return num.includes(pureYear + mm) || new RegExp(pureYear + '[-_/]0?' + sub + '(?!\\d)').test(num)
            })
          } else {
            // AB卷过滤：number 末位字母匹配
            records = records.filter(q => {
              const num = String(q.number || '')
              const last = num.charAt(num.length - 1).toUpperCase()
              const prev = num.charAt(num.length - 2)
              return last === sub.toUpperCase() && prev >= '0' && prev <= '9'
            })
          }
        }
        // 按题号排序，确保题目顺序正确
        records.sort((a, b) => {
          const numA = String(a.number || a.id || '').match(/\d+/)
          const numB = String(b.number || b.id || '').match(/\d+/)
          if (numA && numB) {
            return parseInt(numA[0]) - parseInt(numB[0])
          }
          return 0
        })
        this.practiceQuestions = records
        const n = this.practiceQuestions.length
        this.questionResults = []
        this.wrongQuestions = []
        this.currentIndex = 0
        this.questionStartTimes = new Array(n).fill(null)
        this.questionDurations = new Array(n).fill(0)
        this.userAnswers = new Array(n).fill('')
        this.uploadedImageFiles = new Array(n).fill(null)
        this.imageFileLists = new Array(n).fill(null).map(() => [])
        this.startQuestionTimer()
        this.resetTimer()
        this.startTimer()
        // 不在进入年份时自动创建“进行中”记录，避免列表被污染
        this.currentExamRecord = null

        // 如果该年份已完成，则按 answerDetail 回显
        const statusRecord = this.getYearStatus(key) || this.getYearStatus(pureYear)
        if (statusRecord && Number(statusRecord.status) === 1 && statusRecord.answerDetail) {
          this.applyCompletedRecord(statusRecord)
        }
      } catch (e) {
        console.error('按年份获取题目失败:', e)
        this.error = '获取该年份题目失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },

    parseAnswerDetail(detail) {
      if (!detail) return []
      if (Array.isArray(detail)) return detail
      try {
        const parsed = JSON.parse(detail)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },

    applyCompletedRecord(record) {
      const detailArr = this.parseAnswerDetail(record && record.answerDetail)
      const byQid = new Map()
      for (const r of detailArr) {
        if (!r) continue
        const qid = Number(r.questionId)
        if (!qid) continue
        byQid.set(qid, r)
      }

      const n = Array.isArray(this.practiceQuestions) ? this.practiceQuestions.length : 0
      const answers = new Array(n).fill('')
      const results = new Array(n).fill(null)

      for (let i = 0; i < n; i++) {
        const q = this.practiceQuestions[i]
        const qid = Number(q && q.id)
        const r = qid ? byQid.get(qid) : null
        const userAnswer = r && r.userAnswer != null ? String(r.userAnswer) : ''
        const correctAnswer = (r && r.correctAnswer != null) ? String(r.correctAnswer) : (q && q.answer ? String(q.answer) : '')
        const isCorrect = !!(r && r.isCorrect)
        answers[i] = userAnswer
        results[i] = {
          questionId: q && q.id,
          questionContent: q && q.content,
          userAnswer,
          correctAnswer,
          isCorrect,
          analysis: q && q.analysis,
          knowledge: q && q.knowledge,
          aiInfo: r ? (r.aiInfo || null) : null
        }
      }

      this.userAnswers = answers
      this.questionResults = results
      this.wrongQuestions = results.filter(r => r && !r.isCorrect)
      this.hasSubmitted = true

      // 已完成：停止计时（展示历史结果即可）
      this.pauseTimer()
    },

    // 开始考试记录
    async startExamRecord(year, totalCount) {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId || !this.repoId) return

        const resp = await startPastPaperExam({
          userId: userId,
          repoId: Number(this.repoId) || this.repoId,
          year: String(year),
          totalCount: totalCount
        })
        this.currentExamRecord = resp?.data?.data ?? resp?.data ?? null
      } catch (e) {
        console.warn('创建考试记录失败:', e)
      }
    },

    // 完成考试记录
    async finishExamRecord(results) {
      try {
        if (!this.currentExamRecord || !this.currentExamRecord.id) return

        const answeredCount = results.filter(r => r && r.userAnswer && String(r.userAnswer).trim() !== '').length
        const correctCount = results.filter(r => r && r.isCorrect).length
        const wrongCount = answeredCount - correctCount
        const totalCount = this.practiceQuestions.length
        const scoreRate = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0

        // 按 GESPPractice 的字段风格显式传参，避免后端忽略字段
        await finishPastPaperExam({
          id: this.currentExamRecord.id,
          userId: this.currentExamRecord.userId,
          repoId: Number(this.repoId) || this.repoId,
          year: String(this.selectedYear),
          startTime: this.currentExamRecord.startTime,
          status: 1,
          totalCount,
          answeredCount,
          correctCount,
          wrongCount,
          scoreRate,
          duration: this.elapsedSeconds,
          answerDetail: JSON.stringify(results)
        })

        // 更新本地状态
        await this.loadYearStatus()
      } catch (e) {
        console.warn('完成考试记录失败:', e)
      }
    },

    // 按难度筛选（进入年份后应用到当前集合）
    filterByDifficulty(difficulty) {
      // 此方法保留以兼容，但不直接改动原集合，仅在 enterYear 后初始化时使用
      this.resetAnswers()
    },

    // 年份卡片徽标：普通→年份，月份→"N月"，卷别→"X卷"
    formatYearBadge(key) {
      const parts = String(key).split('_')
      if (parts.length === 1) return parts[0]
      const sub = parts.slice(1).join('_')
      if (/^\d+$/.test(sub)) return sub + '月'
      return sub + '卷'
    },

    // 年份卡片标题完整描述
    formatYearLabel(key) {
      const parts = String(key).split('_')
      const year = parts[0]
      if (parts.length === 1) return year + (this.langMode === 'zh' ? '年' : '')
      const sub = parts.slice(1).join('_')
      if (/^\d+$/.test(sub)) return year + '年 ' + sub + '月'
      return year + ' ' + sub + '卷'
    },

    // 年份题量显示（后端未返回时为 0）
    formatYearCount(y) {
      const key1 = y
      const key2 = String(y)
      const counts = this.yearCounts || {}
      const val = counts[key1] ?? counts[key2]
      const num = Number(val)
      return Number.isFinite(num) && num > 0 ? num : 0
    },

    // 解析选项（兼容字符串/对象/数组）
    parseOptions(raw) {
      if (!raw) return []
      // 1) 已是数组
      if (Array.isArray(raw)) {
        // {key:'A', value:'...'} 或 'A. ...' / '(A) ...'
        return raw.map(item => {
          if (typeof item === 'string') {
            const m = item.match(/^\s*\(?([A-Ea-e])\)?[\.|\)]?\s*(.*)$/)
            return m ? { key: m[1].toUpperCase(), value: m[2].trim() } : { key: '', value: item }
          }
          if (item && typeof item === 'object') {
            const k = (item.key || item.label || '').toString().toUpperCase()
            const v = item.value != null ? String(item.value) : (item.text || '')
            return { key: k, value: (v || '').trim() }
          }
          return { key: '', value: String(item) }
        }).filter(o => o.key)
      }
      // 2) 对象 {A:'...', B:'...'}
      if (typeof raw === 'object') {
        return Object.keys(raw).map(k => ({ key: k.toUpperCase(), value: String(raw[k] || '').trim() }))
      }
      // 3) 字符串
      const optionsStr = String(raw)
      const options = []
      // 3.1 先解析 (A) xxx; (B) yyy; ...
      const reParen = /\(([A-Ea-e])\)\s*([^;\n\r]*)/g
      let m
      while ((m = reParen.exec(optionsStr)) !== null) {
        options.push({ key: m[1].toUpperCase(), value: m[2].trim() })
      }
      if (options.length) return options
      // 3.2 解析 A. xxx 或 A) xxx，按换行或分号拆分
      const parts = optionsStr.split(/\n|;|<br\s*\/>|<br>/i).map(s => s.trim()).filter(Boolean)
      for (const p of parts) {
        const m2 = p.match(/^\s*([A-Ea-e])\s*[\.|\)]\s*(.*)$/)
        if (m2) options.push({ key: m2[1].toUpperCase(), value: m2[2].trim() })
      }
      return options
    },

    // 统一获取当前语言下的选项
    getOptions(question) {
      if (!question) return []
      // 优先根据语言选择
      let source = null
      if (this.langMode === 'en') {
        source = question.optionsEn || question.options_en || null
      } else if (this.langMode === 'zh') {
        source = question.optionsCn || question.options_cn || null
      } else { // bi
        source = question.optionsCn || question.optionsEn || null
      }
      // 退回通用字段
      if (!source) source = question.options || question.optionsStr || question.option || ''
      return this.parseOptions(source)
    },

    // 本地文件上传钩子（与模板绑定）
    handleBeforeUpload() {
      return false
    },
    handleFileChange(index, file, fileList) {
      if (file && file.raw) {
        this.$set(this.uploadedImageFiles, index, file.raw)
        this.$set(this.imageFileLists, index, fileList)
        try { this.$message.success('已选择图片，将在提交时参与AI判题') } catch (e) { /* no-op */ }
      }
    },
    handleRemoveImage(index) {
      this.$set(this.uploadedImageFiles, index, null)
      this.$set(this.imageFileLists, index, [])
    },

    // 获取选项完整内容（(A) xxx）
    getFullOptionContent(question, optionKey) {
      if (!optionKey || !question || (!question.options && !question.optionsStr)) return optionKey || ''
      const opts = this.parseOptions(question.options || question.optionsStr)
      const found = opts.find(o => o.key === optionKey)
      return found ? `${found.key} ${found.value}` : optionKey
    },

    // 答案变化处理
    onAnswerChange(index) {
      // 第一次作答时再创建考试记录（进行中）
      if (!this.currentExamRecord || !this.currentExamRecord.id) {
        try {
          const totalCount = Array.isArray(this.practiceQuestions) ? this.practiceQuestions.length : 0
          if (this.selectedYear && totalCount > 0) {
            this.startExamRecord(this.selectedYear, totalCount)
          }
        } catch (e) {}
      }
      try { console.log(`第${index + 1}题答案:`, this.userAnswers[index]) } catch (e) {}
    },

    // 检查答案是否正确
    isAnswerCorrect(index) {
      const userAnswer = this.userAnswers[index]
      const correctAnswer = this.practiceQuestions[index].answer
      return userAnswer === correctAnswer
    },

    // 获取答案状态类
    getAnswerStatusClass(index) {
      if (!this.userAnswers[index]) return ''
      return this.isAnswerCorrect(index) ? 'correct' : 'incorrect'
    },

    // 获取答案状态文本
    getAnswerStatusText(index) {
      if (!this.userAnswers[index]) return ''
      return this.isAnswerCorrect(index) ? '正确' : '错误'
    },

    // 获取难度类
    getDifficultyClass(difficulty) {
      const level = this.getDifficultyLevel(difficulty)
      return `difficulty-${level}`
    },

    // 根据数字难度获取难度级别
    getDifficultyLevel(difficulty) {
      if (difficulty <= 0.3) return 'easy'
      if (difficulty <= 0.7) return 'medium'
      return 'hard'
    },

    // 获取难度类型
    getDifficultyType(difficulty) {
      const level = this.getDifficultyLevel(difficulty)
      const typeMap = {
        'easy': 'success',
        'medium': 'warning',
        'hard': 'danger'
      }
      return typeMap[level] || 'info'
    },

    // 获取难度文本
    getDifficultyText(difficulty) {
      const level = this.getDifficultyLevel(difficulty)
      const textMap = {
        'easy': '简单',
        'medium': '中等',
        'hard': '困难'
      }
      return textMap[level] || '未知'
    },

    // 格式化题目内容
    formatQuestionContent(content) {
      if (!content) return '暂无题目内容'
      // 简单的HTML格式化，可以根据需要扩展
      return content.replace(/\n/g, '<br>')
    },

    // 简易多语言
    tt(key) {
      const zh = {
        pageTitle: '历年真题练习',
        refresh: '刷新题目',
        loading: '正在加载题目...',
        reload: '重新加载',
        questionCount: '题目数',
        startPractice: '开始练习',
        totalQuestions: '题目总数',
        answeredCount: '已答题数',
        correctCount: '答对题数',
        accuracy: '正确率',
        backToYears: '返回年份列表',
        uploadImage: '上传图片',
        imageOnlyTip: '仅支持图片格式，大小建议≤10MB',
        uploadStepsTip: '上传解题步骤（可选，提交时一并判题）',
        uploadedImages: '已上传的图片：',
        preview: '预览',
        delete: '删除',
        inputAnswer: '请输入答案',
        answerTip: '（请输入答案，如：A、B、C、D、E 或具体数值）',
        correctAnswer: '正确答案：',
        analysis: '解析：',
        prev: '上一题',
        next: '下一题',
        question: '第',
        submit: '提交答案',
        hideAnswers: '隐藏答案',
        showAnswers: '显示答案',
        reset: '重置答案',
        empty: '暂无题目数据',
        imagePreview: '图片预览',
        resultTitle: '答题结果',
        close: '关闭',
        retry: '重新练习',
        totalLabel: '总题数：',
        previewImageAlt: '上传图片',
        wrongAnalysis: '错题分析',
        yourAnswer: '你的答案：',
        elapsed: '用时',
        start: '开始',
        pause: '暂停',
        reset: '重置',
        completed: '已完成',
        inProgress: '进行中',
        scoreRate: '得分率',
        reviewAgain: '再次练习'
      }
      const en = {
        pageTitle: 'AMC Past Papers Practice',
        refresh: 'Refresh',
        loading: 'Loading questions...',
        reload: 'Reload',
        questionCount: 'Questions',
        startPractice: 'Start Practice',
        totalQuestions: 'Total Questions',
        answeredCount: 'Answered',
        correctCount: 'Correct',
        accuracy: 'Accuracy',
        backToYears: 'Back to years',
        uploadImage: 'Upload Image',
        imageOnlyTip: 'Images only, recommended size ≤10MB',
        uploadStepsTip: 'Upload solution steps (optional; judged with submission)',
        uploadedImages: 'Uploaded images:',
        preview: 'Preview',
        delete: 'Delete',
        inputAnswer: 'Enter your answer',
        answerTip: '(Enter an answer, e.g., A–E or a value)',
        correctAnswer: 'Correct answer:',
        analysis: 'Analysis:',
        prev: 'Previous',
        next: 'Next',
        question: 'Question',
        submit: 'Submit',
        hideAnswers: 'Hide answers',
        showAnswers: 'Show answers',
        reset: 'Reset',
        empty: 'No question data',
        imagePreview: 'Image Preview',
        resultTitle: 'Results',
        close: 'Close',
        retry: 'Retry',
        totalLabel: 'Total:',
        previewImageAlt: 'Uploaded image ',
        wrongAnalysis: 'Wrong answers analysis',
        yourAnswer: 'Your answer:',
        elapsed: 'Time',
        start: 'Start',
        pause: 'Pause',
        reset: 'Reset',
        completed: 'Completed',
        inProgress: 'In Progress',
        scoreRate: 'Score',
        reviewAgain: 'Review Again'
      }
      const dict = this.langMode === 'en' ? en : zh
      return dict[key] || key
    },

    // 计时器相关
    startTimer() {
      if (this.timerRunning) return
      this.timerRunning = true
      if (this.timerId) clearInterval(this.timerId)
      this.timerId = setInterval(() => {
        this.elapsedSeconds += 1
      }, 1000)
    },
    pauseTimer() {
      this.timerRunning = false
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
    },
    resetTimer() {
      this.pauseTimer()
      this.elapsedSeconds = 0
    },
    clearTimer() {
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
      this.timerRunning = false
    },
    formatTime(totalSec) {
      const s = Math.max(0, Number(totalSec) || 0)
      const hh = Math.floor(s / 3600)
      const mm = Math.floor((s % 3600) / 60)
      const ss = s % 60
      const pad = (n) => String(n).padStart(2, '0')
      return hh > 0 ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`
    },

    // 题头年份标签：只显示数字
    formatYearTag(val) {
      return String(val || '')
    },

    // 题号文本
    formatQuestionNumber(index) {
      const n = Number(index) + 1
      if (this.langMode === 'en') return `Question ${n}`
      if (this.langMode === 'bi') return `第 ${n} 题 / Question ${n}`
      return `第 ${n} 题`
    },

    // 渲染数学公式（支持 \(...\) / \[...\] / $$...$$ / 行内 $...$）
    renderMathFormula(text) {
      if (!text) return ''
      try {
        let html = String(text)
        // 行内: \(...\)
        // 修复：使用 [\s\S]+? 而不是 [^)]+ 以正确匹配包含括号的公式
        html = html.replace(/\\\(([\s\S]+?)\\\)/g, (match, formula) => {
          try { return katex.renderToString(formula, { throwOnError: false, displayMode: false }) } catch (e) { return match }
        })
        // 块级: \[...\]
        // 修复：使用 [\s\S]+? 而不是 [^\]]+ 以正确匹配复杂公式
        html = html.replace(/\\\[([\s\S]+?)\\\]/g, (match, formula) => {
          try { return katex.renderToString(formula, { throwOnError: false, displayMode: true }) } catch (e) { return match }
        })
        // 块级: $$...$$
        html = html.replace(/\$\$([\s\S]+?)\$\$/g, (match, formula) => {
          try { return katex.renderToString(formula, { throwOnError: false, displayMode: true }) } catch (e) { return match }
        })
        // 行内: $...$，避免与 $$ 冲突
        html = html.replace(/\$(?!\$)([^$]+?)\$(?!\$)/g, (match, formula) => {
          try { return katex.renderToString(formula, { throwOnError: false, displayMode: false }) } catch (e) { return match }
        })
        return html
      } catch (e) {
        return String(text)
      }
    },

    // 统一获取题干（中/英/双语）
    getQuestionContent(question, mode) {
      if (!question) return '暂无题目内容'
      const cn = question.contentCn || question.contentcn || question.content || ''
      const en = question.contentEn || question.contenten || ''
      const uni = question.content || ''
      const m = mode || this.langMode || 'bi'
      if (m === 'zh') return cn || uni
      if (m === 'en') return en || uni
      if (cn && en) return `${cn}<br/><br/>${en}`
      return cn || en || uni
    },

    // 统一获取解析（中/英/双语）
    getQuestionAnalysis(question) {
      if (!question) return ''
      const cn = question.analysisCn || question.analysiscn || ''
      const en = question.analysisEn || question.analysisen || ''
      const uni = question.analysis || ''
      const m = this.langMode || 'bi'
      if (m === 'zh') return cn || uni
      if (m === 'en') return en || uni
      if (cn && en) return `${cn}<br/><br/>${en}`
      return cn || en || uni
    },

    // 解析并返回题目的图片 URL（支持绝对、/api/file/、以及 /{bucket}/{key}）
    resolveImageUrl(path) {
      if (!path) return ''
      const s = String(path)
      if (/^(https?:)?\/\//i.test(s)) return s
      if (s.startsWith('/api/file/')) return s
      if (/^\/[A-Za-z0-9_-]+\//.test(s)) {
        try { return getMinioUrl(s) } catch (_) {}
      }
      const base = process.env.VUE_APP_BASE_API || ''
      try {
        const absBase = /^(https?:)?\/\//i.test(base)
          ? base
          : (window.location.origin + (base.startsWith('/') ? base : ('/' + base)))
        const url = new URL(s, absBase)
        return url.toString()
      } catch (_) {
        return base ? (base.replace(/\/$/, '') + '/' + s.replace(/^\//, '')) : s
      }
    },
    // 获取题目的所有图片路径（支持 imagePaths JSON 字符串）
    getQuestionImages(question) {
      if (!question) return []
      const images = []

      // 1. 检查 imagePaths 字段（JSON 字符串格式）
      const imagePaths = question.imagePaths || question.image_paths
      if (imagePaths) {
        try {
          const paths = typeof imagePaths === 'string' ? JSON.parse(imagePaths) : imagePaths
          if (Array.isArray(paths)) {
            images.push(...paths.filter(p => p))
          }
        } catch (e) {
          // 如果解析失败，尝试作为单个路径
          if (typeof imagePaths === 'string' && imagePaths.trim()) {
            images.push(...imagePaths.split(/[;,]/).map(p => p.trim()).filter(Boolean))
          } else if (Array.isArray(imagePaths)) {
            images.push(...imagePaths.filter(p => p))
          }
        }
      }

      // 2. 检查 image 字段（向后兼容）
      if (question.image && !images.includes(question.image)) {
        images.push(question.image)
      }

      // 3. 检查 img 字段（向后兼容）
      if (question.img && !images.includes(question.img)) {
        images.push(question.img)
      }

      // 4. 检查 url 字段（向后兼容）
      if (question.url && !images.includes(question.url)) {
        images.push(question.url)
      }

      return images.filter(img => img && img.trim())
    },

    // 判断题目是否有选项图片
    hasOptionImages(question) {
      if (!question) return false
      const raw = question.optionImages || question.option_images
      if (!raw) return false
      if (Array.isArray(raw)) return raw.some(item => String(item || '').trim())
      if (typeof raw === 'string') return !!raw.trim()
      return false
    },

    // 获取指定索引的选项图片
    getOptionImage(question, optionIndex) {
      if (!question) return null
      const raw = question.optionImages || question.option_images
      if (!raw) return null

      let imagePaths = []
      if (Array.isArray(raw)) {
        imagePaths = raw.map(item => String(item || '').trim()).filter(Boolean)
      } else if (typeof raw === 'string') {
        const optionImages = raw.trim()
        if (!optionImages) return null
        try {
          const parsed = JSON.parse(optionImages)
          if (Array.isArray(parsed)) {
            imagePaths = parsed.map(item => String(item || '').trim()).filter(Boolean)
          }
        } catch (e) {
          imagePaths = optionImages.split(/[;,]/).map(p => p.trim()).filter(Boolean)
        }
      }
      return imagePaths[optionIndex] || null
    },

    // 获取题型文本
    getQuestionTypeText(quType) {
      const typeMap = {
        1: '选择题',
        2: '填空题',
        3: '解答题',
        4: '判断题'
      }
      return typeMap[quType] || '未知题型'
    },

    // 提交答案
    async submitAnswers() {
      // 如果有未答题目，弹确认框
      const unanswered = this.practiceQuestions.length - this.answeredCount
      if (unanswered > 0) {
        const confirmed = await this.$confirm(
          `还有 ${unanswered} 道题未作答，确定要提交吗？未作答题目将计为错误。`,
          '提交确认',
          { type: 'warning', confirmButtonText: '确定提交', cancelButtonText: '继续作答' }
        ).catch(() => false)
        if (!confirmed) return
      }

      // 记录当前题目用时
      this.recordQuestionTime()

      this.submitting = true
      this.pauseTimer()

      try {
        // 若尚未创建考试记录，则在提交时创建（避免只进入年份就创建记录）
        if (!this.currentExamRecord || !this.currentExamRecord.id) {
          await this.startExamRecord(this.selectedYear, this.practiceQuestions.length)
        }
        // 逐题判定：本地判定为基础，若有本地图片文件则调用 AI 判题进行纠偏（不走上传接口）
        const results = []
        for (let index = 0; index < this.practiceQuestions.length; index++) {
          const question = this.practiceQuestions[index]
          const userAnswer = this.userAnswers[index]
          const correctAnswer = question.answer
          const hasAnswer = !!(userAnswer && String(userAnswer).trim() !== '')
          let isCorrect = hasAnswer ? (userAnswer === correctAnswer) : false
          let aiInfo = null

          // 若该题有本地图片文件，则直接附带到判题请求
          const file = this.uploadedImageFiles[index]
          try { console.log('[PastPapers] submit index', index, { hasFile: !!file, userAnswer, correctAnswer }) } catch (e) {}
          if (file && hasAnswer) {
            try {
              console.log('[PastPapers] AI judging for index', index)
              const formData = new FormData()
              formData.append('question', question.content || question.contentCn || question.contentcn || question.contentEn || question.contenten || '')
              formData.append('user_answer', this.getFullOptionContent(question, userAnswer))
              formData.append('correct_answer', this.getFullOptionContent(question, correctAnswer))
              formData.append('explanation', question.analysis || question.analysisCn || question.analysiscn || question.analysisEn || question.analysisen || question.judge || '')
              formData.append('image', file, file.name)

              let loadingService = null
              try {
                loadingService = this.$loading({ lock: true, text: 'AI分析中...', spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.35)' })
                const resp = await judgeSolution(formData)
                const parsed = this.parseAiJudge(resp)
                if (parsed) {
                  aiInfo = parsed
                  if (typeof parsed.answerCorrect === 'boolean') {
                    isCorrect = parsed.answerCorrect
                  }
                }
              } finally {
                if (loadingService) loadingService.close()
              }
            } catch (e) {
              // AI 判题失败则保留本地判定
              console.error('AI判题失败，保留本地判定:', e?.response || e)
            }
          } else {
            try {
              if (!hasAnswer) {
                console.log('[PastPapers] skip AI: no userAnswer at index', index)
              } else {
                console.log('[PastPapers] skip AI: no file at index', index)
              }
            } catch (e) {}
          }

          results.push({
            questionId: question.id,
            questionContent: question.content,
            userAnswer: hasAnswer ? userAnswer : '',
            correctAnswer: correctAnswer,
            isCorrect: isCorrect,
            analysis: question.analysis,
            knowledge: question.knowledge,
            aiInfo
          })
        }

        this.questionResults = results
        this.wrongQuestions = results.filter(r => !r.isCorrect)
        this.showResultDialog = true
        try { console.log('[PastPapers] results count', results.length) } catch (e) {}

        // 标记已提交，退出时不再提示
        this.hasSubmitted = true

        // 异步写入用户作答记录与 AMC 汇总
        this.$nextTick(() => { this.persistPastPapersRecords() })

        await this.syncStudyProgressBatch(results)

        // 完成考试记录
        await this.finishExamRecord(results)

        // 显示总结信息
        if (this.finalScore === 100) {
          this.$message.success(`恭喜！全部答对！得分：${this.finalScore}分`)
        } else if (this.finalScore >= 80) {
          this.$message.success(`很好！答对${this.correctCount}题，得分：${this.finalScore}分`)
        } else if (this.finalScore >= 60) {
          this.$message.warning(`继续加油！答对${this.correctCount}题，得分：${this.finalScore}分`)
        } else {
          this.$message.error(`需要加强练习！答对${this.correctCount}题，得分：${this.finalScore}分`)
        }
      } catch (error) {
        console.error('提交答案失败:', error)
        this.$message.error('提交答案失败，请重试')
      } finally {
        this.submitting = false
      }
    },

    // 切换显示答案
    toggleShowAnswers() {
      this.showAnswers = !this.showAnswers
    },

    // 重置答案
    resetAnswers() {
      const n = Array.isArray(this.practiceQuestions) ? this.practiceQuestions.length : 0
      this.userAnswers = new Array(n).fill('')
      this.questionResults = []
      this.wrongQuestions = []
      this.showAnswers = false
      this.hasSubmitted = false
      // 同步重置本地图片文件
      this.uploadedImageFiles = new Array(n).fill(null)
      this.imageFileLists = new Array(n).fill(null).map(() => [])
    },

    // 关闭结果弹窗
    handleCloseResultDialog() {
      this.showResultDialog = false
      this.wrongQuestions = []
    },

    // 清除某年份的做题记录和真题状态记录（再次练习前调用）
    async clearYearRecords(year) {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId || !this.repoId) return
        const quesIds = (this.practiceQuestions || []).map(q => q.id).filter(Boolean)
        const statusIdsToDelete = await this.getPastPaperStatusIdsToDelete(userId, quesIds)
        await Promise.allSettled([
          deletePastPaperByYear(userId, Number(this.repoId), year),
          quesIds.length ? deleteRecordsByQuesIds(userId, Number(this.repoId), quesIds) : Promise.resolve(),
          statusIdsToDelete.length ? userStatusBatchDel(statusIdsToDelete) : Promise.resolve()
        ])
        await this.loadYearStatus()
      } catch (e) {
        console.warn('清除年份记录失败:', e)
      }
    },

    async getPastPaperStatusIdsToDelete(userId, quesIds) {
      if (!userId || !this.repoId || !Array.isArray(quesIds) || !quesIds.length) return []
      const statusResp = await userStatusByUser(userId, Number(this.repoId) || this.repoId)
      const rawStatusList = statusResp?.data?.data ?? statusResp?.data ?? []
      const statusList = Array.isArray(rawStatusList) ? rawStatusList : []
      const quesIdSet = new Set(quesIds.map(id => String(id)))
      return statusList.filter(item => {
        if (!item || !String(item.source || '').endsWith('真题')) return false
        const rawQuesList = String(item.quesList || item.ques_list || '').trim()
        if (!rawQuesList) return false
        const itemQuesIds = rawQuesList.split(/[;,，、\s]+/).map(v => String(v).trim()).filter(Boolean)
        return itemQuesIds.some(id => quesIdSet.has(id))
      }).map(item => item.id).filter(Boolean)
    },

    async clearGespYearRecords(key) {
      try {
        const userInfo = getTokenInfo()
        const userId = userInfo ? userInfo.id : null
        if (!userId || !this.repoId) return
        const parts = String(key).split('_')
        const year = parts[0]
        const month = parts.length > 1 ? parts.slice(1).join('_') : ''
        const level = this.resolveGespLevel(this.$route.query.courseType || '')

        const [recordResp, questionResp] = await Promise.all([
          listPastPaperRecords(userId, this.repoId),
          pagingByRepoId(this.repoId, {
            pageNum: 1,
            pageSize: 2000,
            year: month && /^\d+$/.test(month) ? undefined : year
          })
        ])

        const rawRecords = recordResp?.data?.data ?? recordResp?.data ?? []
        const records = Array.isArray(rawRecords) ? rawRecords : []
        const matchedRecord = records.find(record => {
          if (!record || String(record.year || '') !== String(year || '')) return false
          const detail = this.parsePastPaperAnswerDetail(record.answerDetail || record.answer_detail)
          const meta = detail && detail.meta && typeof detail.meta === 'object' ? detail.meta : {}
          const recordMonth = meta.month != null && String(meta.month) !== '' ? String(meta.month) : ''
          const recordLevel = meta.level != null && String(meta.level) !== '' ? String(meta.level) : ''
          if (month && String(recordMonth) !== String(month)) return false
          if (level && recordLevel && String(recordLevel) !== String(level)) return false
          return true
        })

        const questionRaw = questionResp?.data?.data ?? questionResp?.data ?? {}
        let questions = Array.isArray(questionRaw?.records) ? questionRaw.records : (Array.isArray(questionRaw?.list) ? questionRaw.list : (Array.isArray(questionRaw) ? questionRaw : []))
        if (month) {
          const mm = String(month).padStart(2, '0')
          const yearStr = String(year)
          questions = questions.filter(q => {
            const num = String(q?.number || '')
            return num.includes(yearStr + mm) || new RegExp(yearStr + '[-_/]0?' + month + '(?!\\d)').test(num)
          })
        }
        const quesIds = questions.map(q => q && q.id).filter(Boolean)
        const statusIdsToDelete = await this.getPastPaperStatusIdsToDelete(userId, quesIds)

        await Promise.allSettled([
          matchedRecord && matchedRecord.id ? deletePastPaperById(matchedRecord.id) : Promise.resolve(),
          quesIds.length ? deleteRecordsByQuesIds(userId, Number(this.repoId), quesIds) : Promise.resolve(),
          statusIdsToDelete.length ? userStatusBatchDel(statusIdsToDelete) : Promise.resolve()
        ])

        const statusKey = this.resolveYearStatusKey(key)
        if (statusKey && this.yearStatusMap[String(statusKey)]) {
          this.$delete(this.yearStatusMap, String(statusKey))
        }
        await this.loadYearStatus()
      } catch (e) {
        console.warn('清除 GESP 真题记录失败:', e)
      }
    },

    // 重新练习
    async retryPractice() {
      this.showResultDialog = false
      await this.clearYearRecords(this.selectedYear)
      this.resetAnswers()
    },

    // 获取错题在原题目列表中的索引
    getOriginalQuestionIndex(questionId) {
      if (!this.practiceQuestions || !questionId) return 0
      const index = this.practiceQuestions.findIndex(q => q.id === questionId)
      return index >= 0 ? index : 0
    },

    // 刷新题目
    refreshQuestions() {
      if (this.selectedYear && this.shouldWarnOnExit()) {
        const ok = window.confirm('刷新将导致本次作答作废，确定要刷新题目吗？')
        if (!ok) return
      }
      if (this.selectedYear) {
        this.loadYearQuestions(this.selectedYear)
      } else {
        this.loadYears()
      }
    },

    // 单题模式：上一题/下一题
    prevQuestion() {
      if (this.currentIndex > 0) {
        this.recordQuestionTime()
        this.currentIndex -= 1
        this.startQuestionTimer()
        this.$nextTick(() => { window.scrollTo(0, 0) })
      }
    },
    nextQuestion() {
      if (this.currentIndex < this.practiceQuestions.length - 1) {
        this.recordQuestionTime()
        this.currentIndex += 1
        this.startQuestionTimer()
        this.$nextTick(() => { window.scrollTo(0, 0) })
      }
    },
    // 跳转到指定题目
    jumpToQuestion(index) {
      if (index >= 0 && index < this.practiceQuestions.length && index !== this.currentIndex) {
        this.recordQuestionTime()
        this.currentIndex = index
        this.startQuestionTimer()
        this.$nextTick(() => { window.scrollTo(0, 0) })
      }
    },
    // 开始当前题目计时
    startQuestionTimer() {
      if (this.currentIndex >= 0 && this.currentIndex < this.questionStartTimes.length) {
        this.questionStartTimes[this.currentIndex] = Date.now()
      }
    },
    // 记录当前题目用时
    recordQuestionTime() {
      if (this.currentIndex >= 0 && this.currentIndex < this.questionStartTimes.length) {
        const startTime = this.questionStartTimes[this.currentIndex]
        if (startTime) {
          const duration = (Date.now() - startTime) / 1000 // 转为秒
          this.questionDurations[this.currentIndex] = parseFloat(duration.toFixed(2))
        }
      }
    },

    // 返回上一页
    goBack() {
      // 先关闭可能残留的加载遮罩
      this.loading = false
      this.pauseTimer()
      this.clearTimer()
      this.$router.replace({
        path: '/amc-course',
        query: {
          repoId: this.repoId || undefined,
          level: this.$route.query.level || undefined,
          courseType: this.$route.query.courseType || undefined,
          courseTypeId: this.$route.query.courseTypeId || undefined,
          courseTypeCode: this.$route.query.courseTypeCode || undefined
        }
      })
    },

    // 本地文件上传钩子（阻止自动上传，走本地存储）
    handleBeforeUpload() {
      return false
    },
    handleFileChange(index, file, fileList) {
      if (file && file.raw) {
        this.$set(this.uploadedImageFiles, index, file.raw)
        this.$set(this.imageFileLists, index, fileList)
        try { this.$message.success('已选择图片，将在提交时参与AI判题') } catch (e) { /* no-op */ }
      }
    },
    handleRemoveImage(index) {
      this.$set(this.uploadedImageFiles, index, null)
      this.$set(this.imageFileLists, index, [])
    },

    // 图片上传成功
    handleImageUploadSuccess(response, file, questionIndex) {
      console.log('图片上传成功:', response)
      if (response && response.data && response.data.url) {
        if (!this.questionImages[questionIndex]) {
          this.$set(this.questionImages, questionIndex, [])
        }
        this.questionImages[questionIndex].push({
          url: response.data.url,
          name: file.name,
          size: file.size
        })
        this.$message.success('图片上传成功!')
      } else {
        this.$message.error('图片上传失败，请重试')
      }
    },

    // 图片上传失败
    handleImageUploadError(error) {
      console.error('图片上传失败:', error)
      this.$message.error('图片上传失败，请重试')
    },

    // 预览图片
    previewImage(url) {
      this.previewImageUrl = url
      this.showImagePreview = true
    },

    // 解析 AI 判题响应（仅提取判正确性和分析，不弹窗）
    parseAiJudge(apiResponse) {
      try {
        const raw = apiResponse?.data ?? apiResponse
        const data = raw?.result ?? raw?.data ?? raw
        if (!data) return null
        const analysis = data.ai_analysis || null
        const answerCorrect = analysis && typeof analysis.answer_correct !== 'undefined'
          ? !!analysis.answer_correct
          : null
        if (answerCorrect === null) return null
        return { answerCorrect, analysis }
      } catch (e) {
        return null
      }
    },

    // 删除图片
    removeImage(questionIndex, imageIndex) {
      this.$confirm('确定要删除这张图片吗？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.questionImages[questionIndex].splice(imageIndex, 1)
        this.$message.success('图片已删除')
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    }
  }
}
</script>

<style scoped>
.year-select-content {
  padding: 28px;
}
.year-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
}
.year-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #e6f0ff 0%, #f5f9ff 100%);
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.year-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.12);
}
.year-card-completed {
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border: 2px solid #4caf50;
}
.year-card-completed .year-badge {
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
}
.year-card-locked {
  background: #f5f7fa;
  opacity: 0.7;
  border: 2px solid #f56c6c;
  cursor: not-allowed;
}
.year-card-locked:hover {
  transform: none;
  box-shadow: 0 2px 8px rgba(31, 45, 61, 0.06);
}
.year-card-locked .year-badge {
  background: linear-gradient(135deg, #909399 0%, #c0c4cc 100%);
}
.year-card-locked .year-title {
  color: #909399;
}
.year-card-locked .year-sub {
  color: #c0c4cc;
}
.year-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.score-rate {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}
.year-badge {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 22px;
  box-shadow: inset 0 2px 6px rgba(255,255,255,0.2);
}
.year-info {
  flex: 1;
}
.year-title {
  font-weight: 600;
  color: #2c3e50;
  font-size: 18px;
}
.year-sub {
  color: #909399;
  font-size: 14px;
  margin-top: 4px;
}
.enter-btn {
  position: absolute;
  right: 16px;
  top: 16px;
  padding: 6px 12px;
  font-size: 12px;
}
.amc-past-papers-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 0;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 顶部导航栏 */
.amc-header {
  background: #fff;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-right {
  display: flex;
  align-items: center;
}

.back-btn {
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background-color: #f5f5f5;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

/* 加载和错误状态 */
.loading-container, .error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.error-content {
  text-align: center;
  color: #666;
}

.error-icon {
  font-size: 48px;
  color: #f56c6c;
  margin-bottom: 20px;
}

.error-message {
  font-size: 16px;
  margin-bottom: 20px;
}

/* 题目练习区域 */
.question-practice-content {
  padding: 20px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.filter-controls {
  display: flex;
  gap: 15px;
}

/* 题目容器 */
.questions-container {
  display: grid;
  gap: 20px;
  margin-bottom: 30px;
}

.question-item {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.question-item.answered {
  border-color: #e6f7ff;
  background: #f6ffed;
}

.question-item.submitted-wrong {
  background: #fff2f0;
  border-color: #ff4d4f;
}

.question-item.submitted-correct {
  background: #f6ffed;
  border-color: #52c41a;
}

.post-submit-panel {
  margin-top: 14px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px dashed rgba(255, 77, 79, 0.35);
}

.post-submit-answer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.post-submit-answer .correct-label {
  font-weight: 600;
  color: #333;
}

.post-submit-answer .correct-value {
  font-weight: 700;
  color: #ff4d4f;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.question-number {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.question-meta {
  display: flex;
  gap: 8px;
  align-items: center;
}

.question-year, .question-knowledge {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  background: #f0f0f0;
  color: #666;
}

.question-difficulty {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.difficulty-easy {
  background: #f6ffed;
  color: #52c41a;
}

.difficulty-medium {
  background: #fff7e6;
  color: #fa8c16;
}

.difficulty-hard {
  background: #fff2f0;
  color: #ff4d4f;
}

.answer-status {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.answer-status.correct {
  background: #f6ffed;
  color: #52c41a;
}

.answer-status.incorrect {
  background: #fff2f0;
  color: #ff4d4f;
}

.question-content {
  margin-bottom: 15px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

.question-stem-images {
  margin: 8px 0 12px;
}

.stem-image {
  display: block;
  width: auto;
  max-width: 420px;
  margin: 8px auto;
}

.stem-image /deep/ .el-image__inner {
  width: auto;
  height: auto !important;
  max-width: 100%;
  max-height: 320px;
  object-fit: contain;
}

/* 图片上传区域 */
.image-upload-section {
  margin-bottom: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #d9d9d9;
}

.upload-button-container {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.image-uploader {
  display: inline-block;
}

.upload-tip {
  font-size: 12px;
  color: #999;
}

.uploaded-images {
  margin-top: 15px;
}

.image-preview-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 10px;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.image-item {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fff;
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-item:hover .image-actions {
  opacity: 1;
}

.image-actions .el-button {
  color: #fff;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.image-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* 图片预览弹窗 */
.image-preview-dialog {
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.question-options {
  margin-bottom: 15px;
}

.option-item {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
}

.question-answer-input {
  margin-bottom: 15px;
}

.answer-tip {
  font-size: 12px;
  color: #999;
  margin-left: 10px;
}

.correct-answer {
  background: #f6ffed;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 10px;
}

.correct-label {
  font-weight: 500;
  color: #52c41a;
}

.correct-value {
  color: #52c41a;
  font-weight: 600;
  margin-left: 5px;
}

.analysis-section {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-top: 10px;
}

.analysis-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.analysis-content {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

/* 结果弹窗 */
.result-summary {
  margin-bottom: 20px;
}

.result-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.result-stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.stat-label {
  font-weight: 500;
  color: #666;
}

.stat-value {
  font-weight: 600;
  color: #333;
}

.stat-value.correct {
  color: #52c41a;
}

.stat-value.wrong {
  color: #ff4d4f;
}

.stat-value.score {
  color: #1890ff;
  font-size: 18px;
}

.wrong-questions-section {
  margin-top: 20px;
}

.wrong-questions-section h3 {
  color: #333;
  margin-bottom: 15px;
}

.wrong-question-item {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
}

.answer-details {
  margin: 10px 0;
}

.answer-row {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.answer-label {
  font-weight: 500;
  color: #666;
  margin-right: 10px;
}

.answer-value {
  font-weight: 600;
}

.answer-value.incorrect {
  color: #ff4d4f;
}

.answer-value.correct {
  color: #52c41a;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 20px;
  display: block;
}

.empty-state p {
  margin: 0;
  font-size: 16px;
}

/* 分页 */
.pagination-section {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .amc-header {
    padding: 10px 15px;
    flex-direction: column;
    gap: 10px;
  }

  .header-left {
    width: 100%;
    justify-content: space-between;
  }

  .header-right {
    width: 100%;
    justify-content: center;
  }

  .questions-section {
    padding: 15px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .filter-controls {
    width: 100%;
    flex-direction: column;
  }

  .question-card {
    padding: 15px;
  }

  .image-upload-section {
    padding: 10px;
  }

  .upload-button-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .image-list {
    gap: 10px;
  }

  .image-item {
    width: 80px;
    height: 80px;
  }
}

/* 题目总览导航栏 */
.question-nav-panel {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.question-nav-panel.collapsed .nav-content {
  max-height: 0;
  overflow: hidden;
}

.nav-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 15px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  cursor: pointer;
  user-select: none;
  transition: background 0.3s ease;
}

.nav-header:hover {
  background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%);
}

.nav-title {
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-title i {
  font-size: 14px;
  transition: transform 0.3s ease;
}

.nav-overview-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
  margin-right: 8px;
}

.nav-overview-stat-item {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 8px 12px;
  min-width: 90px;
  text-align: center;
  backdrop-filter: blur(8px);
}

.nav-overview-stat-number {
  font-size: 20px;
  line-height: 1.1;
  font-weight: 700;
}

.nav-overview-stat-label {
  margin-top: 2px;
  font-size: 12px;
  opacity: 0.9;
}

.nav-stats {
  font-size: 14px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 12px;
}

.nav-content {
  padding: 20px;
  max-height: 500px;
  overflow-y: auto;
  transition: max-height 0.3s ease;
}

.nav-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  gap: 10px;
}

.nav-item {
  position: relative;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #606266;
}

.nav-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.nav-item.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: #fff;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.nav-item.answered {
  background: #e8f4fd;
  border-color: #409eff;
  color: #409eff;
}

.nav-item.correct {
  background: #f0f9ff;
  border-color: #52c41a;
  color: #52c41a;
}

.nav-item.correct::after {
  content: '✓';
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px;
  color: #52c41a;
}

.nav-item.wrong {
  background: #fff2f0;
  border-color: #ff4d4f;
  color: #ff4d4f;
}

.nav-item.wrong::after {
  content: '✗';
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px;
  color: #ff4d4f;
}

.nav-item-number {
  font-size: 14px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .nav-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .nav-overview-stats {
    width: 100%;
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .nav-overview-stat-item {
    min-width: 86px;
    padding: 6px 10px;
  }

  .nav-overview-stat-number {
    font-size: 16px;
  }

  .nav-overview-stat-label {
    font-size: 11px;
  }

  .nav-stats {
    align-self: flex-end;
  }

  .nav-grid {
    grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
    gap: 8px;
  }

  .nav-item {
    width: 45px;
    height: 45px;
  }

  .nav-item-number {
    font-size: 13px;
  }
}
</style>
